import {
  isTicketListSocketPayload,
  type TicketListSocketItem,
  type TicketListSocketPayload,
  type TicketUpdatedSocketPayload,
} from '@21k/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { TicketListItem, UserProfile } from '../api/types';
import { useSocket, useSocketReconnect } from '../contexts/SocketContext';
import {
  socketItemToListItem,
  ticketMatchesListFilters,
  type TicketListFilters,
  userCanAcceptLiveTicket,
} from '../utils/ticketListLiveSync';

const NEW_TICKET_HIGHLIGHT_MS = 3000;

export function useTicketListLiveSync({
  user,
  filters,
  tickets,
  setTickets,
  setTotal,
  page,
  pageSize,
  onRefetch,
}: {
  user: UserProfile | null;
  filters: TicketListFilters;
  tickets: TicketListItem[];
  setTickets: React.Dispatch<React.SetStateAction<TicketListItem[]>>;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  pageSize: number;
  onRefetch: () => void;
}) {
  const { socket } = useSocket();
  const filtersRef = useRef(filters);
  const ticketsRef = useRef(tickets);
  const userRef = useRef(user);
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  const seenNewIdsRef = useRef(new Set<string>());
  const [newTicketIds, setNewTicketIds] = useState<Set<string>>(() => new Set());
  const [pendingNewCount, setPendingNewCount] = useState(0);

  useEffect(() => {
    filtersRef.current = filters;
    setPendingNewCount(0);
  }, [filters]);

  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    pageRef.current = page;
    pageSizeRef.current = pageSize;
  }, [page, pageSize]);

  const markTicketNew = useCallback((ticketId: string) => {
    setNewTicketIds((prev) => new Set(prev).add(ticketId));
    window.setTimeout(() => {
      setNewTicketIds((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
    }, NEW_TICKET_HIGHLIGHT_MS);
  }, []);

  const handleIncomingNew = useCallback(
    (incoming: TicketListSocketItem) => {
      const currentUser = userRef.current;
      if (!currentUser) return;
      if (!userCanAcceptLiveTicket(currentUser, incoming)) return;
      if (!ticketMatchesListFilters(incoming, filtersRef.current, currentUser.id)) return;
      if (seenNewIdsRef.current.has(incoming.id)) return;
      if (ticketsRef.current.some((t) => t.id === incoming.id)) {
        seenNewIdsRef.current.add(incoming.id);
        return;
      }

      seenNewIdsRef.current.add(incoming.id);
      const listItem = socketItemToListItem(incoming);
      if (pageRef.current <= 1) {
        setTickets((prev) => {
          if (prev.some((t) => t.id === incoming.id)) {
            const seen = new Set<string>();
            return prev.filter((t) => {
              if (seen.has(t.id)) return false;
              seen.add(t.id);
              return true;
            });
          }
          return [listItem, ...prev].slice(0, pageSizeRef.current);
        });
        markTicketNew(incoming.id);
      }
      setTotal((count) => count + 1);
      setPendingNewCount((count) => count + 1);
    },
    [markTicketNew, setTickets, setTotal],
  );

  const handleIncomingUpdate = useCallback(
    (incoming: TicketListSocketItem) => {
      const currentUser = userRef.current;
      if (!currentUser) return;
      if (!userCanAcceptLiveTicket(currentUser, incoming)) return;

      setTickets((prev) => {
        const index = prev.findIndex((t) => t.id === incoming.id);
        if (index < 0) return prev;
        const next = [...prev];
        next[index] = socketItemToListItem(incoming);
        return next;
      });
    },
    [setTickets],
  );

  useEffect(() => {
    if (!socket || !user) return;

    const onTicketNew = (payload: TicketListSocketPayload) => {
      handleIncomingNew(payload.ticket);
    };

    const onTicketUpdated = (payload: TicketUpdatedSocketPayload) => {
      if (!isTicketListSocketPayload(payload)) return;
      handleIncomingUpdate(payload.ticket);
    };

    socket.on('ticket:new', onTicketNew);
    socket.on('ticket:updated', onTicketUpdated);

    return () => {
      socket.off('ticket:new', onTicketNew);
      socket.off('ticket:updated', onTicketUpdated);
    };
  }, [socket, user, handleIncomingNew, handleIncomingUpdate]);

  useSocketReconnect(onRefetch);

  const dismissNewBanner = useCallback(() => {
    setPendingNewCount(0);
  }, []);

  const scrollToFirstNew = useCallback(() => {
    const firstNewId = [...newTicketIds][0];
    if (!firstNewId) return;
    const row = document.querySelector(`[data-ticket-id="${firstNewId}"]`);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    dismissNewBanner();
  }, [newTicketIds, dismissNewBanner]);

  return {
    newTicketIds,
    pendingNewCount,
    dismissNewBanner,
    scrollToFirstNew,
  };
}
